import { Component, input, OnDestroy, OnInit, output } from '@angular/core';
import { PosMarketSearchParameter } from '../../../service/model/pos';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { PosElement } from '../../../service/model/pos/pos-element';
import { select, Store } from '@ngrx/store';
import { SetPosSearchValueAction } from '../../../store/pos/pos-actions';
import { selectPosSearchValue } from '../../../store/pos/pos-selector';
import { UserDetailsService } from '../../../service/user-details.service';
import { PosMarketServiceElements } from '../../../service/model/pos/pos-market-service-elements';
import { SearchCriteriaComponent } from '../../../base/search-criteria/search-criteria.component';
import { UppComponentNames } from '../../../service/model';
import { selectNavigation } from '../../../store/navigation/navigation-selector';
import { LookupOptions } from '../../../core/util/lookup-options';
import { NgClass } from '@angular/common';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { LoadingAlertComponent } from '../../../components/loading-alert/loading-alert.component';
import { MultiSelectBadgesComponent } from '../../../components/multi-select-badges/multi-select-badges.component';
import { PosState } from '../../../store/pos/pos-state';
import { ConfigurationService } from '../../../service/configuration/configuration.service';
import { FeatureFlags } from '../../../core/util/resources';
import { alphanumericSpaceMax30 } from '../../../service/model/common/validators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'ama-ng-upp-pos-search-criteria',
  templateUrl: './pos-search-criteria.component.html',
  styleUrls: ['./pos-search-criteria.component.scss'],
  standalone: true,
  imports: [LoadingAlertComponent, ReactiveFormsModule, NgbTooltip, NgClass, MultiSelectBadgesComponent]
})
export class PosSearchCriteriaComponent extends SearchCriteriaComponent implements OnInit, OnDestroy {
  sendingStatus = input<boolean>(false);

  searchPos = output<PosMarketSearchParameter>();
  clearPos = output<boolean>();

  readonly COMPONENT_NAME = UppComponentNames.POS;

  posSearchCriteriaForm!: UntypedFormGroup;
  readonly = false;

  numberOfItems = 2;

  posElement: PosElement = { location: {}, level: {} };
  searchParameters!: PosMarketSearchParameter;

  regionPasteRegExp = /[,|;]/; // separator regex for ',' and ';'
  lookupOptions?: LookupOptions;
  lookup = false;

  nameRegex = '^[A-Z0-9]{0,30}$';
  spacesAllowed = false;
  private readonly subscription = new Subscription();

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly store: Store<PosState>,
    private readonly userDetailsService: UserDetailsService,
    private readonly configurationService: ConfigurationService
  ) {
    super();
    this.buildRegionsDescriptionOnly();

    this.subscription.add(
      this.configurationService.getParameter$(FeatureFlags.spacesAllowedInPosAndMarkets).subscribe((isEnabled) => {
        this.spacesAllowed = isEnabled;
        this.nameRegex = isEnabled ? alphanumericSpaceMax30 : '^[A-Z0-9]{0,30}$';
        this.posSearchCriteriaForm
          ?.get('name')
          ?.setValidators([Validators.maxLength(30), Validators.pattern(this.nameRegex)]);
        this.posSearchCriteriaForm?.get('name')?.updateValueAndValidity();
      })
    );
  }

  get includeFormGroup(): UntypedFormGroup {
    return this.posSearchCriteriaForm.get(PosMarketServiceElements.INCLUDE) as UntypedFormGroup;
  }

  ngOnInit(): void {
    this.createAndLoadSearchForm();
    this.subscription.add(
      this.store.select(selectNavigation).subscribe((lookupParams) => (this.lookupOptions = lookupParams))
    );
    this.lookup = this.lookupOptions?.lookup ?? false;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  createAndLoadSearchForm() {
    this.posSearchCriteriaForm = this.formBuilder.group({
      name: this.formBuilder.control('', [Validators.maxLength(30), Validators.pattern(this.nameRegex)]),
      include: this.formBuilder.group({
        world: this.formBuilder.control(false)
      })
    });
    setTimeout(() => {
      this.subscription.add(
        this.store.pipe(select(selectPosSearchValue)).subscribe((pos) => {
          if (!this.lookup && pos) {
            this.posSearchCriteriaForm.patchValue(pos);
          }
        })
      );
    }, 0);
  }

  clearClicked() {
    this.posSearchCriteriaForm.setValue({
      name: '',
      include: {
        world: false,
        regions: [],
        countries: [],
        cities: [],
        airports: [],
        officeIds: [],
        corporateCodesAndQualifiers: []
      }
    });
    this.clearPos.emit(true);
  }

  searchClicked() {
    const regionCodes = this.getRegionCodesForDescriptions(
      this.posSearchCriteriaForm.get([PosMarketServiceElements.INCLUDE, PosMarketServiceElements.REGIONS])?.value
    );
    this.posElement = {
      location: {
        world: this.posSearchCriteriaForm.get([PosMarketServiceElements.INCLUDE, PosMarketServiceElements.WORLD])
          ?.value,
        regions: regionCodes,
        countries: this.posSearchCriteriaForm.get([
          PosMarketServiceElements.INCLUDE,
          PosMarketServiceElements.COUNTRIES
        ])?.value,
        cities: this.posSearchCriteriaForm.get([PosMarketServiceElements.INCLUDE, PosMarketServiceElements.CITIES])
          ?.value,
        airports: this.posSearchCriteriaForm.get([PosMarketServiceElements.INCLUDE, PosMarketServiceElements.AIRPORTS])
          ?.value
      },
      level: {
        officeIds: this.posSearchCriteriaForm.get([
          PosMarketServiceElements.INCLUDE,
          PosMarketServiceElements.OFFICE_IDS
        ])?.value,
        corporateCodesAndQualifiers: this.posSearchCriteriaForm.get([
          PosMarketServiceElements.INCLUDE,
          PosMarketServiceElements.CORPORATE_CODES
        ])?.value
      }
    };
    this.initializeSearchParameters();
  }

  initializeSearchParameters() {
    if (this.userDetailsService.loggedInUser?.partitions) {
      this.searchParameters = {
        partitionId: this.userDetailsService.loggedInUser.selectedPartition,
        element: this.posElement
      };
    } else {
      this.searchParameters = {
        organization: this.userDetailsService.getLssOrganization(),
        element: this.posElement
      };
    }
    const name = this.posSearchCriteriaForm.get(PosMarketServiceElements.NAME)?.value as string | undefined;
    if (name && name.length > 0) {
      this.searchParameters.name = name;
    }

    this.emitSearchParameters();
  }

  // Search-parameters(form value) will be emitted to the PosSearchComponent, in order to create a search request.
  emitSearchParameters() {
    this.searchPos.emit(this.searchParameters);
  }

  storeUnsavedPosSearch() {
    this.store.dispatch(new SetPosSearchValueAction({ value: this.posSearchCriteriaForm.value }));
  }
}
